import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SafeImage from "../../SafeImage";
import Tooltip from "rc-tooltip";
import "rc-tooltip/assets/bootstrap.css";
import clsx from "clsx";
import PropTypes from "prop-types";
import { CloseButton } from "js/components/shared/CloseButton";
import BlankFavicon from "@/assets/studentTile/favicons/blank-favicon.svg?url";

const Tab = (props) => {
	const { t } = useTranslation();
	const [width, setWidth] = useState(null);
	const [size, setSize] = useState(null);

	const contentRef = useRef(null);
	const url = props.tab.url || props.tab.tabUrl || "";
	const favicon = props.tab.favicon || props.tab.favIcon;
	const isInternal = url.startsWith("chrome") || url.startsWith("edge");

	const measure = () => {
		if (contentRef && contentRef.current) {
			const width = contentRef.current.getBoundingClientRect().width;
			setWidth(width);
		}
	};

	useEffect(() => {
		window.addEventListener("resize", measure);
		return () => {
			window.removeEventListener("resize", measure);
		};
	}, []);

	useEffect(() => {
		measure();
	}, [props.tabCount]);

	useEffect(
		function () {
			if (width > 220) {
				setSize("large");
			} else if (width > 160) {
				setSize("medium");
			} else {
				setSize("small");
			}
		},
		[width]
	);

	const getActiveTabBadgeText = (size) => {
		switch (size) {
			case "large":
				return "Active Tab";
			case "medium":
				return "Active";
			case "small":
			default:
				return "";
		}
	};

	return (
		<div
			className={clsx("P2PDisplayModal_tab", props.selected ? "P2PDisplayModal_tab--selected" : "P2PDisplayModal_tab--inactive", {
				"P2PDisplayModal_tab--lastInactive": !props.selected && props.last,
				"P2PDisplayModal_activeTab--selected": props.isActive && props.selected,
			})}
		>
			<div ref={contentRef} className={props.selected ? "P2PDisplayModal_tabContent--selected" : "P2PDisplayModal_tabContent"}>
				<Tooltip
					placement="bottomLeft"
					trigger={["hover"]}
					id={props.tooltipId}
					destroyTooltipOnHide={false}
					overlayClassName={"Tooltip_OpenNewTabHyperlink"}
					overlay={
						<a href={props.tab.url || props.tab.tabUrl} target="_blank" rel="noopener noreferrer">
							{props.tab.url || props.tab.tabUrl}
							<br />
							Open in new tab
						</a>
					}
				>
					<span
						className={props.selected ? "P2PDisplayModal_tooltipTabContainer--selected" : "P2PDisplayModal_tooltipTabContainer"}
						onClick={() => props.onSelect(props.index)}
					>
						<SafeImage
							styleClassName={clsx(
								props.selected || props.isActive ? "P2PDisplayModal_tabFavicon--activeOrSelected" : "P2PDisplayModal_tabFavicon",
								{
									"P2PDisplayModal_tabContent--hidden": width < 46 && props.isActive && props.canCloseTab,
								}
							)}
							src={isInternal || !favicon ? BlankFavicon : favicon}
						/>
						<span
							className={clsx(props.selected ? "P2PDisplayModal_tabTitleText" : "P2PDisplayModal_tabTitleText--inactive", {
								"P2PDisplayModal_tabContent--hidden": width < 60 && props.selected && props.canCloseTab,
							})}
						>
							{props.tab.title}
						</span>
						{props.isActive && (
							<span className={`P2PDisplayModal_activeTabBadge--${size}`}>
								<span>{getActiveTabBadgeText(size)}</span>
							</span>
						)}
					</span>
				</Tooltip>
				{props.isMonitoringClass || !props.canCloseTab || !props.isStudentClaimed ? null : (
					<CloseButton
						className={props.selected ? "P2PDisplayModal_tabCloseButton--selected" : "P2PDisplayModal_tabCloseButton"}
						tooltipText={t("classroomDetails.closeTab")}
						onClick={props.closeTab}
					/>
				)}
				{!props.selected && !props.last && !props.beforeSelected && <div className="P2PDisplayModal_tabSeparator" />}
			</div>
		</div>
	);
};

Tab.propTypes = {
	selected: PropTypes.bool,
	last: PropTypes.bool,
	beforeSelected: PropTypes.bool,
	isActive: PropTypes.bool,
	tab: PropTypes.object,
	isMonitoringClass: PropTypes.bool,
	isStudentClaimed: PropTypes.bool,
	closeTab: PropTypes.func,
	onSelect: PropTypes.func,
	index: PropTypes.number,
	tooltipId: PropTypes.string,
	tabCount: PropTypes.number,
	canCloseTab: PropTypes.bool,
};

export default Tab;
